import { ReactNode, useCallback, useEffect, useRef } from "react";
import { trpc } from "@/lib/trpc";
import ErrorBoundary from "@/components/ErrorBoundary";

type RuntimeIssue = {
  source: string;
  message: string;
  stack?: string | null;
  pathname?: string | null;
};

const MAX_RECENT_FINGERPRINTS = 100;
const PUBLIC_PATHS = new Set(["/", "/purchase", "/forgot-password", "/login"]);

function normalizedMessage(value: unknown) {
  if (value instanceof Error) return value.message || value.name || "Unknown browser error";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value) || "Unknown browser error";
  } catch {
    return "Unknown browser error";
  }
}

/**
 * Sends only authenticated, de-duplicated client failures to the server. The
 * server performs final sanitization and workspace authorization; this client
 * guard prevents noisy looped events from overwhelming the diagnostics queue.
 */
export function RuntimeIssueReporter({ children }: { children: ReactNode }) {
  const legacyReport = trpc.admin.reportLegacyApplicationIssue.useMutation({ retry: false });
  const tenantReport = trpc.saas.reportWorkspaceApplicationIssue.useMutation({ retry: false });
  const recentlyReported = useRef(new Set<string>());

  const report = useCallback((issue: RuntimeIssue) => {
    if (typeof window === "undefined") return;
    const pathname = issue.pathname || window.location.pathname;
    // These routes are intentionally available without authentication. Do not
    // call protected diagnostics procedures from them; a failed diagnostic
    // request can otherwise cascade into the global auth redirect handler.
    if (PUBLIC_PATHS.has(pathname)) return;
    const key = `${issue.source}:${issue.message}:${pathname}`.slice(0, 512);
    if (recentlyReported.current.has(key)) return;
    recentlyReported.current.add(key);
    if (recentlyReported.current.size > MAX_RECENT_FINGERPRINTS) {
      const oldest = recentlyReported.current.values().next().value;
      if (oldest) recentlyReported.current.delete(oldest);
    }

    const tenantSlug = pathname.match(/^\/tenant\/([^/?#]+)/)?.[1];
    const input = {
      severity: "error" as const,
      source: issue.source.slice(0, 64),
      message: issue.message.slice(0, 2_000),
      stack: issue.stack?.slice(0, 8_000),
      pathname: pathname.slice(0, 1_024),
    };
    if (tenantSlug) {
      tenantReport.mutate({ ...input, slug: tenantSlug });
    } else {
      legacyReport.mutate(input);
    }
  }, [legacyReport, tenantReport]);

  useEffect(() => {
    const onError = (event: ErrorEvent) => report({
      source: "window.error",
      message: event.message || "Unhandled browser error",
      stack: event.error instanceof Error ? event.error.stack : null,
      pathname: window.location.pathname,
    });
    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      report({
        source: "window.unhandledrejection",
        message: normalizedMessage(reason),
        stack: reason instanceof Error ? reason.stack : null,
        pathname: window.location.pathname,
      });
    };
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onUnhandledRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
    };
  }, [report]);

  return <ErrorBoundary onError={(error, componentStack) => report({ source: "react.error-boundary", message: error.message || error.name, stack: `${error.stack || ""}\n${componentStack || ""}`, pathname: typeof window === "undefined" ? null : window.location.pathname })}>{children}</ErrorBoundary>;
}
